import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Loader2, ShieldCheck, KeyRound } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * One-shot maintenance card: runs the v1 → v2 encryption migration on
 * `db.integrations`. Idempotent — re-running shows all rows as "skip"
 * once they're already on v2.
 *
 * Hits POST /api/admin/maintenance/run-encryption-migration.
 * Requires admin JWT + the `X-Migration-Secret` header (server env var).
 */
export default function EncryptionMigrationCard() {
  const [secret, setSecret] = useState('');
  const [running, setRunning] = useState(null); // 'dry' | 'real' | null
  const [report, setReport] = useState(null);

  const callEndpoint = async (dryRun) => {
    if (!secret.trim()) {
      toast.error('Enter the migration secret first.');
      return;
    }
    try {
      setRunning(dryRun ? 'dry' : 'real');
      setReport(null);
      const res = await fetch(
        `${API_URL}/api/admin/maintenance/run-encryption-migration`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Migration-Secret': secret.trim(),
          },
          body: JSON.stringify({ dry_run: dryRun }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || `Failed (HTTP ${res.status})`);
        setReport({ error: data?.detail || `HTTP ${res.status}` });
        return;
      }
      setReport(data);
      const verb = dryRun ? 'Dry-run complete' : 'Migration complete';
      toast.success(
        `${verb}: ${data.migrated} migrated, ${data.skipped} already-v2, ${data.errors} errors`
      );
    } catch (e) {
      toast.error(`Network error: ${e.message}`);
      setReport({ error: e.message });
    } finally {
      setRunning(null);
    }
  };

  return (
    <Card className="border-2 border-amber-200 bg-amber-50/30 dark:bg-amber-950/10" data-testid="encryption-migration-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-amber-700" />
          Encryption Migration (v1 → v2)
          <Badge variant="outline" className="ml-2">
            One-shot
          </Badge>
        </CardTitle>
        <CardDescription className="space-y-1">
          <span className="block">
            Re-encrypts every row in <code>db.integrations</code> from legacy Fernet (v1) to AES-256-GCM (v2).
            Safe to re-run — already-migrated rows are reported as "skip".
          </span>
          <span className="block text-xs text-muted-foreground">
            Run this once per environment after deploying the pycryptodome change. After every row
            reports "skip", you can stop using this card.
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="migration-secret" className="flex items-center gap-2">
            <KeyRound className="h-4 w-4" />
            Migration Secret
          </Label>
          <Input
            id="migration-secret"
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            placeholder="Paste the value of MIGRATION_SECRET from backend/.env"
            data-testid="migration-secret-input"
          />
          <p className="text-xs text-muted-foreground">
            Stored as <code>MIGRATION_SECRET</code> in <code>backend/.env</code>. Ask the deployer
            if you don't have it.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => callEndpoint(true)}
            disabled={running !== null}
            data-testid="migration-dry-run-btn"
          >
            {running === 'dry' ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Dry-running…
              </>
            ) : (
              'Dry Run (preview only)'
            )}
          </Button>
          <Button
            variant="default"
            onClick={() => callEndpoint(false)}
            disabled={running !== null}
            data-testid="migration-run-btn"
          >
            {running === 'real' ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Running…
              </>
            ) : (
              'Run Migration'
            )}
          </Button>
        </div>

        {report && !report.error && (
          <div className="rounded-md border bg-background p-4 space-y-2" data-testid="migration-report">
            <div className="flex flex-wrap gap-2 text-sm">
              <Badge variant={report.success ? 'default' : 'destructive'}>
                {report.success ? 'Success' : 'Errors'}
              </Badge>
              {report.dry_run && <Badge variant="secondary">Dry run</Badge>}
              <Badge variant="outline">Total: {report.total}</Badge>
              <Badge variant="outline">Migrated: {report.migrated}</Badge>
              <Badge variant="outline">Skipped (already-v2): {report.skipped}</Badge>
              <Badge variant={report.errors > 0 ? 'destructive' : 'outline'}>
                Errors: {report.errors}
              </Badge>
            </div>
            <div className="border-t pt-2">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1">Service</th>
                    <th className="py-1">Environment</th>
                    <th className="py-1">Action</th>
                    <th className="py-1">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.rows || []).map((row) => (
                    <tr key={`${row.service}-${row.environment}`} className="border-t">
                      <td className="py-1 font-mono">{row.service}</td>
                      <td className="py-1">{row.environment}</td>
                      <td className="py-1">
                        <Badge
                          variant={
                            row.action === 'error'
                              ? 'destructive'
                              : row.action === 'skip'
                              ? 'secondary'
                              : 'default'
                          }
                        >
                          {row.action}
                        </Badge>
                      </td>
                      <td className="py-1 text-muted-foreground">
                        {row.error || (row.fields != null ? `${row.fields} fields` : '—')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {report?.error && (
          <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 p-3 text-sm text-red-700 dark:text-red-300">
            {report.error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
